import React from "react";
import { MentionsInput, Mention } from "react-mentions";
import { isEmpty } from "../hooks/constants";
import defaultStyle from "@components/MarketVisitReport/defaultStyle";
import defaultMentionStyle from "@components/MarketVisitReport/defaultMentionStyle";
import styles from "@components/MarketVisitReport/MarketVisitReport.module.css";

/**
 * WSSSection Component
 * WSS (Wholesaler) section of Market Visit Report
 * 
 * @param {Object} formData - Form data
 * @param {Function} onChange - Handler for form field changes
 * @param {Array} wssName - Array of visited WSS names
 * @param {Array} productList - List of products for mentions
 * @param {Array} wssList - List of WSS for mentions
 * @param {Array} dealerList - List of dealers for mentions
 * @param {Object} characterLimitValidation - Character limit validation errors
 * @param {Object} formValidation - Form validation errors
 * @param {String} userType - Type of user
 * @param {Boolean} disable - Form disable state
 */
const WSSSection = ({
  formData,
  onChange,
  wssName,
  productList,
  wssList,
  dealerList,
  characterLimitValidation,
  formValidation,
  userType,
  disable,
}) => {
  return (
    <>
      {/* Section Header */}
      <div className={`row m-0 ${styles.MarketVisitReport__headingBox}`}>
        <div className={`col-8 p-0 ${styles.MarketVisitReport__heading}`}>
          WSS
        </div>
      </div>

      {/* WSS Name Visited */}
      <div className={styles.MarketVisitReport__selectableRows}>
        <label className={styles.MarketVisitReport__selectLabel}>
          Name(s) of WSS(s) visited where any issue(s) reported
        </label>
        <input
          className={` ${styles.MarketVisitReport__selectField}`}
          style={{ paddingLeft: "8px" }}
          name="wssVisited"
          value={wssName?.length ? wssName : formData?.wssVisited}
          onChange={(e) => onChange(e)}
          disabled={disable}
          maxLength={256}
        />
        {characterLimitValidation?.wssVisited && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {characterLimitValidation?.wssVisited}
          </p>
        )}
        {!formData?.wssVisited && formValidation?.wssVisited && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {formValidation?.wssVisited}
          </p>
        )}
      </div>

      {/* Key Observations */}
      <div className={styles.MarketVisitReport__selectableRows}>
        <label className={styles.MarketVisitReport__selectLabel}>
          Key Observations/Issues faced by WSS(s)
        </label>
        <MentionsInput
          value={
            isEmpty(formData?.keyObservationWss)
              ? ""
              : formData?.keyObservationWss
          }
          onChange={(e) => {
            onChange({
              target: {
                name: "keyObservationWss",
                value: e.target.value,
              },
            });
          }}
          className={styles.MarketVisitRepor__input}
          style={defaultStyle}
          disabled={disable}
          maxLength={2001}
        >
          <Mention
            trigger="@p"
            data={productList}
            style={defaultMentionStyle}
          />
          <Mention trigger="@w" data={wssList} style={defaultMentionStyle} />
          <Mention trigger="@d" data={dealerList} style={defaultMentionStyle} />
        </MentionsInput>
        <p className={styles.MarketVisitReport__selectAlertMsg}>
          {" "}
          *Use @w for wss Selection
        </p>
        {characterLimitValidation?.keyObservationWss && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {characterLimitValidation?.keyObservationWss}
          </p>
        )}
        {!formData?.keyObservationWss && formValidation?.keyObservationWss && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {formValidation?.keyObservationWss}
          </p>
        )}
      </div>

      {/* Competition Products and Darpan Bills - only for non-MH3/RBDM users */}
      {userType !== "MH3" && userType !== "RBDM Collaborator" && (
        <>
          {/* Competition Products at WSS */}
          <div className={styles.MarketVisitReport__selectableRows}>
            <label className={styles.MarketVisitReport__selectLabel}>
              Are competition products (PIL Similar) available at the WSS?
            </label>
            <div className={`mb-1 ${styles.myUserDetails__radioOption}`}>
              <div className="form-check">
                <input
                  className={`form-check-input ${styles.myUserDetails__option}`}
                  type="radio"
                  name="productWssRadio"
                  id="productWssRadioYes"
                  checked={
                    formData.productWssRadio === "yes" ||
                    formData.productWssRadio === "Yes"
                  }
                  value="yes"
                  onChange={(e) => onChange(e)}
                  disabled={disable}
                />
                <label
                  className={styles.marketVisitReport__radioLabel}
                  htmlFor="productWssRadioYes"
                >
                  Yes
                </label>
              </div>
              <div className="form-check">
                <input
                  className={`form-check-input ${styles.myUserDetails__option}`}
                  type="radio"
                  name="productWssRadio"
                  id="productWssRadioNo"
                  checked={
                    formData.productWssRadio === "no" ||
                    formData.productWssRadio === "No"
                  }
                  value="no"
                  onChange={(e) => onChange(e)}
                  disabled={disable}
                />
                <label
                  className={styles.marketVisitReport__radioLabel}
                  htmlFor="productWssRadioNo"
                >
                  No
                </label>
              </div>
            </div>
            {!formData?.productWssRadio && formValidation?.productWssRadio && (
              <p className={styles.MarketVisitReport__validationErrors}>
                {formValidation?.productWssRadio}
              </p>
            )}
          </div>

          {/* Darpan Bills */}
          <div className={styles.MarketVisitReport__selectableRows}>
            <label className={styles.MarketVisitReport__selectLabel}>
              Are Darpan bills available at all the dealers visited today?
            </label>
            <div className={`mb-1 ${styles.myUserDetails__radioOption}`}>
              <div className="form-check">
                <input
                  className={`form-check-input ${styles.myUserDetails__option}`}
                  type="radio"
                  name="darpanBillRadio"
                  id="darpanBillRadioYes"
                  checked={
                    formData.darpanBillRadio === "yes" ||
                    formData.darpanBillRadio === "Yes"
                  }
                  value="yes"
                  onChange={(e) => onChange(e)}
                  disabled={disable}
                />
                <label
                  className={styles.marketVisitReport__radioLabel}
                  htmlFor="darpanBillRadioYes"
                >
                  Yes
                </label>
              </div>
              <div className="form-check">
                <input
                  className={`form-check-input ${styles.myUserDetails__option}`}
                  type="radio"
                  name="darpanBillRadio"
                  id="darpanBillRadioNo"
                  checked={
                    formData.darpanBillRadio === "no" ||
                    formData.darpanBillRadio === "No"
                  }
                  value="no"
                  onChange={(e) => onChange(e)}
                  disabled={disable}
                />
                <label
                  className={styles.marketVisitReport__radioLabel}
                  htmlFor="darpanBillRadioNo"
                >
                  No
                </label>
              </div>
            </div>
            {!formData?.darpanBillRadio && formValidation?.darpanBillRadio && (
              <p className={styles.MarketVisitReport__validationErrors}>
                {formValidation?.darpanBillRadio}
              </p>
            )}
          </div>

          {/* Darpan Bills Unavailable Dealers */}
          <div className={styles.MarketVisitReport__selectableRows}>
            <label className={styles.MarketVisitReport__selectLabel}>
              Names of Dealers where Darpan bills unavailable
            </label>
            <MentionsInput
              className={styles.MarketVisitRepor__input}
              value={
                isEmpty(formData?.darpanDealer) ? "" : formData?.darpanDealer
              }
              onChange={(e) => {
                onChange({
                  target: {
                    name: "darpanDealer",
                    value: e.target.value,
                  },
                });
              }}
              maxLength={501}
              style={defaultStyle}
              disabled={disable}
            >
              <Mention
                trigger="@p"
                data={productList}
                style={defaultMentionStyle}
              />
              <Mention
                trigger="@d"
                data={dealerList}
                style={defaultMentionStyle}
              />
            </MentionsInput>
            <p className={styles.MarketVisitReport__selectAlertMsg}>
              *Use @d for dealer Selection
            </p>
            {characterLimitValidation?.darpanDealer && (
              <p className={styles.MarketVisitReport__validationErrors}>
                {characterLimitValidation?.darpanDealer}
              </p>
            )}
            {!formData?.darpanDealer && formValidation?.darpanDealer && (
              <p className={styles.MarketVisitReport__validationErrors}>
                {formValidation?.darpanDealer}
              </p>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default WSSSection;
